import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { ConfigContext } from '../context/GlobalContext';
import { toast } from 'react-toastify';
import { AddSaleReturn } from '../data';
import SaleReturnDataValidation from '../helper/SaleReturnDataValidation';
import { Languages } from '../Utils/Languages';
import LoaderView from './LoaderView';

const SaleReturnAdd = () => {

    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();

    const dropdownRef = useRef(null);
    const Invoice_Data = state?.INVOICES?.find(invoice => invoice.invoiceId == state?.SELECTEDINVOICE);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        try {
            const invoiceData = state?.INVOICES?.find(
                invoice => invoice.invoiceId === state?.SELECTEDINVOICE
            );

            if (!invoiceData) throw new Error("Invoice not found.");

            // Find customer from customerId
            const customer = state?.CUSTOMERS?.find(
                cust => cust.customerId === invoiceData.customerId
            );

            if (!customer) throw new Error("Customer not found for this invoice.");

            // Find items from itemId list
            const invoiceItems = invoiceData.items?.map(item =>
                state?.ITEMS?.find(i => i.itemId === item.itemId)
            ).filter(Boolean); // remove undefined if any itemId doesn't match

            if (!invoiceItems.length) throw new Error("No items matched for this invoice.");

            // Enrich item data with invoice item values (e.g., quantity, price from invoice)
            const mergedItems = invoiceData.items.map(invItem => {
                const matchedItem = state?.ITEMS?.find(i => i.itemId === invItem.itemId);

                return {
                    ...matchedItem,
                    ...invItem,
                    originalQuantity: invItem.quantity   // ⭐ IMPORTANT
                };
            });


            // Now update the invoiceData state with enriched customer and items
            setInvoiceData(prev => ({
                ...prev,
                customer: customer?.customer,
                address: customer?.address,
                phone: customer?.phone,
                email: customer?.email,
                customerGSTNo: customer?.customerGSTNo,
                customerId: customer?.customerId,
                items: mergedItems,
            }));

        } catch (error) {
            console.error(error.message);
            toast.error(error.message || "Failed to load invoice details.");
        }
    }, []);


    const [invoiceData, setInvoiceData] = useState({
        id: Invoice_Data?.id,
        creationDate: new Date(parseInt(Invoice_Data?.creationDate?.length > 10 ? Invoice_Data?.creationDate : Invoice_Data?.creationDate * 1000)).getTime(),
        date: new Date(parseInt(Invoice_Data?.date.length > 10 ? Invoice_Data?.date : Invoice_Data?.date * 1000)).getTime(),
        dateDue: new Date(parseInt(Invoice_Data?.dueDate.length > 10 ? Invoice_Data?.dueDate : Invoice_Data?.dueDate * 1000)).getTime(),
        customer: Invoice_Data?.customer,
        address: Invoice_Data?.address,
        phone: Invoice_Data?.phone,
        email: Invoice_Data?.email,
        customerGSTNo: Invoice_Data?.customerGSTNo,
        items: Invoice_Data?.items,
        totalAmount: Invoice_Data?.totalAmount,
        gstPercentage: Invoice_Data?.gstPercetage,
        totalAmount: Invoice_Data?.totalAmount,
        discountPercetage: Invoice_Data?.discountPercetage,
        discountAmount: Invoice_Data?.discountAmount,
        isPaid: Invoice_Data?.paidFlagKey == "true" ? true : false,
        note: Invoice_Data?.note,
        invoiceId: Invoice_Data?.invoiceId,
        companyId: Invoice_Data?.companyId,
        updatedDate: new Date().getTime(),
        isigst: Invoice_Data?.isigst == "true" ? true : false,
        subtotal: 0.00,
        customInvoiceId: Invoice_Data?.customInvoiceId,
        reason: "",
        currency: Invoice_Data?.currency || "",
    });

    const [error, setError] = useState({
        items: [],
    })

    const calculateTotals = () => {
        const totalAmount = invoiceData?.items?.reduce((sum, item) => {
            const price = parseFloat(item?.price || 0);
            const qty = parseFloat(item?.quantity || 0);
            return sum + (price * qty);
        }, 0);


        // Ensure discountPercetage is valid
        const discountPercetage = invoiceData?.discountPercetage || 0;
        const discountAmount = (totalAmount * discountPercetage) / 100;

        // Subtotal after discount
        const subtotalAfterDiscount = totalAmount - discountAmount;

        // Ensure gstPercentage is valid
        const gstPercentage = invoiceData?.gstPercentage || 0;
        const gstAmount = (subtotalAfterDiscount * gstPercentage) / 100;

        // Final grand total after applying GST
        const grandTotal = subtotalAfterDiscount + gstAmount;

        // Update state with new calculated values
        setInvoiceData(prevData => ({
            ...prevData,
            discountAmount: discountAmount?.toFixed(2),
            gstAmount: gstAmount?.toFixed(2),
            totalAmount: grandTotal?.toFixed(2),
            subtotal: totalAmount?.toFixed(2)
        }));
    };

    // Auto-update totalAmount and gstAmount when items or gstPercentage change
    useEffect(() => {
        calculateTotals();
    }, [invoiceData?.items, invoiceData?.gstPercentage, invoiceData?.discountPercetage]);

    const handleChange = (field, value) => {
        setInvoiceData({ ...invoiceData, [field]: value });
        setError({ ...error, [field]: null })
    };


    const handleItemChange = (index, field, value) => {

        setInvoiceData((prevData) => ({
            ...prevData,
            items: prevData.items.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            ),
        }));

        setError((prevError) => ({
            ...prevError,
            items: prevError.items.map((error, i) =>
                i === index ? { ...error, [field]: null } : error
            ),
        }));
    };


    const isLimitReached = React.useMemo(() => {
        const selectedBusinessData = state?.BUSINESSES?.find(b => String(b.companyId) === String(invoiceData.companyId));
        if (selectedBusinessData?.isShared) return false;

        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const freePlanLimit = Number(process.env.REACT_APP_FREE_PLAN_INVOICE_LIMIT || 2);
        const starterPlanLimit = Number(process.env.REACT_APP_STARTER_PLAN_INVOICE_LIMIT || 120);

        const currentMonthSalesReturn = (state?.SALERETURN || []).filter(sr => {
            const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(sr.companyId));
            if (business?.isShared) return false;
            const date = new Date(Number(sr.creationDate));
            return (
                date.getFullYear() === currentYear &&
                date.getMonth() === currentMonth
            );
        }).length;

        const currentYearSalesReturn = (state?.SALERETURN || []).filter(sr => {
            const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(sr.companyId));
            if (business?.isShared) return false;
            const date = new Date(Number(sr.creationDate));
            return date.getFullYear() === currentYear;
        }).length;

        const userPlanName = state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";
        const isFreePlan = userPlanName === "Free Plan";
        const isStarterPlan = userPlanName === process.env.REACT_APP_STARTER_PLAN_NAME;

        if (isFreePlan && currentMonthSalesReturn >= freePlanLimit) return true;
        if (isStarterPlan && currentYearSalesReturn >= starterPlanLimit) return true;

        return false;
    }, [state?.SALERETURN, state?.BUSINESSES, invoiceData.companyId, state?.PAYMENT]);

    const RemoveInvoiceItem = (srNo) => {
        setInvoiceData(prevState => ({
            ...prevState,
            items: prevState.items
                .filter((item, index) => index !== srNo)
                .map((item, index) => ({ ...item, srNo: index + 1 }))
        }));
    };


    const OnPressSave = async () => {
        const invalidItem = invoiceData?.items?.find(
            item => Number(item.quantity) > Number(item.originalQuantity)
        );

        if (invalidItem) {
            toast.error(`You cannot return more than ${invalidItem.originalQuantity} items.`);
            return;
        }

        // ⭐ STEP 2 — Existing Validation
        const { isValid, errors } = await SaleReturnDataValidation(invoiceData);

        if (!isValid) {
            setError(errors);
            return;
        }

        // ⭐ STEP 3 — API Call
        if (invoiceData?.companyId) {
            setIsLoading(true);

            const selectedBusiness = state?.BUSINESSES?.find(b => b.companyId === invoiceData.companyId);
            const targetUid = selectedBusiness?.isShared ? selectedBusiness.ownerUid : state?.USERID;

            const { success, message } = await AddSaleReturn(
                state,
                dispatch,
                invoiceData,
                targetUid
            );

            setIsLoading(false);

            if (success && message === "Return invoice added successfully!") {
                navigate(-1);
                toast.success("Return invoice successfully created!");
            } else {
                toast.error("Failed to create the return invoice, Please try again!");
            }
        }
    };

    const isAUD = invoiceData?.currency === "AUD" || state?.CURRENCYCODE === "AUD" || state?.TAXTYPE === "ABN";
    const isINR = invoiceData?.currency === "INR" || state?.CURRENCYCODE === "INR";

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    {/* Left Side - Back/Close Button */}
                    <div className="d-flex align-items-center gap-2">
                        <button
                            onClick={() => { navigate(-1) }}
                        >
                            <i class="fa fa-arrow-left" style={{ fontSize: 18 }}></i>
                        </button>
                        <p className='fw-semibold mb-0' style={{ fontSize: 20 }}>{Languages[state?.LANGUAGE]?.SaleReturn_add}</p>
                    </div>
                </div>
            </div>
            <div className="card-body py-20">
                <div className="row justify-content-center" id="invoice">
                    <div className="col-lg-12">
                        <div className="shadow-4 border radius-8">
                            <div className="px-20 py-10 border-bottom">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    {/* Invoice ID - Left */}
                                    <h3 className="text-xl mb-0">
                                        {Languages[state?.LANGUAGE]?.invoice} #{invoiceData.id}
                                    </h3>

                                    <div className="d-flex justify-content-between align-items-center gap-2">
                                        <div>
                                            <label className="floting-label">{Languages[state?.LANGUAGE]?.creation_Date}</label>
                                            <input
                                                type="date"
                                                className={"form-control"}
                                                readOnly
                                                id="dueDateInput"
                                                value={invoiceData?.date && new Date(parseInt(invoiceData?.date)).toISOString().split("T")[0]}
                                                onChange={(e) => handleChange("dateDue", new Date(e.target.value).getTime())}
                                                placeholder="Due Date"
                                                style={{ fontSize: 14 }}
                                            />
                                        </div>

                                        <div>
                                            <label className="floting-label">{Languages[state?.LANGUAGE]?.due_date}</label>
                                            <input
                                                type="date"
                                                className={`form-control ${error.dateDue ? "border-danger" : ""}`}
                                                readOnly
                                                id="dueDateInput"
                                                value={invoiceData?.dateDue && new Date(parseInt(invoiceData?.dateDue)).toISOString().split("T")[0]}
                                                onChange={(e) => handleChange("dateDue", new Date(e.target.value).getTime())}
                                                placeholder="Due Date"
                                                style={{ fontSize: 14 }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row justify-content-between g-3 mt-2">
                                    {/* <h6 className="text-md">Customer Details:</h6> */}

                                    <div className="col-12">
                                        <div className="row mt-2">
                                            <div className="col-sm-6 col-md-4 mb-3 d-flex align-items-center gap-3">
                                                <div style={{ width: "100%", position: "relative" }}>
                                                    <label className="floting-label">
                                                        {Languages[state?.LANGUAGE]?.customer_name}
                                                    </label>

                                                    {/* Dropdown with Input */}
                                                    <div className={`form-control d-flex align-items-center p-0 custom-dropdown-bg`}>
                                                        <div className="dropdown w-100 " ref={dropdownRef}>
                                                            <input
                                                                className="form-control border-0 text-start"
                                                                type="button"
                                                                value={invoiceData?.customer}
                                                                style={{
                                                                    background: "transparent",
                                                                    outline: "none",
                                                                    fontSize: 14,
                                                                    paddingLeft: 20,
                                                                }}
                                                                readOnly
                                                            />

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                className="col-sm-6 col-md-4 mb-3 d-flex align-items-center gap-3"
                                            >
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        position: "relative",
                                                    }}
                                                >
                                                    <label className="floting-label" >{Languages[state?.LANGUAGE]?.email}</label>
                                                    <input
                                                        type="email"
                                                        className="form-control ms-1"
                                                        value={invoiceData?.email}
                                                        readOnly
                                                        placeholder="Enter Email"
                                                        style={{ fontSize: 14 }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-6 col-md-4 mb-3 d-flex align-items-center gap-3">
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        position: "relative",
                                                    }}
                                                >
                                                    <label className="floting-label" >{Languages[state?.LANGUAGE]?.phone_number}</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control ms-1"
                                                        value={invoiceData?.phone}
                                                        readOnly
                                                        placeholder="Enter Phone Number"
                                                        style={{ fontSize: 14 }}
                                                    />
                                                </div>

                                            </div>

                                            <div className="col-sm-6 col-md-4 mb-3">
                                                <label className="floting-label" >{(isAUD || state?.TAXTYPE === "ABN") ? "ABN" : state?.TAXTYPE} Number</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={invoiceData?.customerGSTNo}
                                                    readOnly
                                                    placeholder={`Enter ${(isAUD || state?.TAXTYPE === "ABN") ? "ABN" : state?.TAXTYPE} Number`}
                                                    style={{ fontSize: 14, width: "100%" }}
                                                />
                                            </div>

                                            <div className="col-sm-12 col-md-8 mb-3">
                                                <label className="floting-label" >{Languages[state?.LANGUAGE]?.address}</label>
                                                <input
                                                    className="form-control flex-grow-1"
                                                    value={invoiceData?.address}
                                                    readOnly
                                                    placeholder="Enter Address"
                                                    style={{ fontSize: 14 }}
                                                ></input>
                                            </div>

                                        </div>
                                        <div className="row mt-4">
                                            <div className="col-12">

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="py-10 px-20">
                                <div className="mb-4">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table text-sm w-100" id="invoice-table">
                                            <thead>
                                                <tr>
                                                    <th className="text-sm">{Languages[state?.LANGUAGE]?.s_r}</th>
                                                    <th className="text-sm">{Languages[state?.LANGUAGE]?.items}</th>

                                                    <th className="text-sm">
                                                        {state?.UNIT == "Qty" ? Languages[state?.LANGUAGE].qty : Languages[state?.LANGUAGE].hours}
                                                    </th>
                                                    <th className="text-sm">{Languages[state?.LANGUAGE]?.price}</th>
                                                    <th className="text-sm">{Languages[state?.LANGUAGE]?.total}</th>
                                                    <th className="text-center text-sm">{Languages[state?.LANGUAGE]?.action}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoiceData?.items?.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                placeholder="Enter Item"
                                                                value={item.name}
                                                                readOnly
                                                                style={{ width: "100%", maxWidth: "150px" }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                placeholder="0"
                                                                value={item.quantity}
                                                                onChange={(e) => {
                                                                    handleItemChange(
                                                                        index,
                                                                        "quantity",
                                                                        e.target.value
                                                                    );
                                                                    if (item.price) {
                                                                        handleItemChange(
                                                                            index,
                                                                            "amount",
                                                                            e.target.value * item.price
                                                                        );
                                                                    }
                                                                }}
                                                                style={{ width: "80px" }}
                                                            />
                                                            {error.items[index]?.quantity && (
                                                                <span className="tooltip-container text-danger ms-2">
                                                                    <i className="fa-solid fa-circle-exclamation"></i>
                                                                    <span className="tooltip-text">{Languages[state?.LANGUAGE].qty_error}</span>
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                placeholder="0.00"
                                                                value={item.price}
                                                                style={{ width: "100px" }}
                                                                readOnly
                                                            />
                                                        </td>
                                                        <td>
                                                            {state?.SYMBOL === "$"
                                                                ? `${state?.CURRENCYCODE} ${state?.SYMBOL}`
                                                                : `${state?.SYMBOL}`
                                                            }{" "}
                                                            {(parseFloat(item.price || 0) * parseFloat(item.quantity || 0)).toFixed(2)}
                                                        </td>

                                                        <td className="text-center">
                                                            <button type="button" className="remove-row">
                                                                <Icon icon="ic:twotone-close" className="text-danger-main text-xl" onClick={() => RemoveInvoiceItem(index)} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex flex-wrap justify-content-between gap-2">
                                        <div>
                                            <div className="mt-3">
                                                <label className="floting-label">{Languages[state?.LANGUAGE]?.SaleReturn_Reason} </label>
                                                <textarea
                                                    name="#0"
                                                    className="form-control"
                                                    rows={3}
                                                    cols={40}
                                                    placeholder={Languages[state?.LANGUAGE]?.SaleReturn_Reason_enter}
                                                    value={invoiceData?.reason}
                                                    onChange={(e) => {
                                                        handleChange("reason", e.target.value);
                                                    }}
                                                />
                                            </div>

                                            <div className="mt-3">
                                                <label className="floting-label">{Languages[state?.LANGUAGE]?.note} </label>
                                                <textarea
                                                    name="note"
                                                    className="form-control"
                                                    rows={3}
                                                    cols={40}
                                                    value={invoiceData?.note}
                                                    readOnly
                                                />
                                            </div>

                                        </div>

                                        <div>
                                            <table className="text-sm">
                                                <tbody>
                                                    <tr>
                                                        <td className="pe-64">
                                                            <div className="mb-1 d-flex align-items-center">
                                                                <label className="me-2 fw-600">
                                                                    {Languages[state?.LANGUAGE]?.subtotal}{" "}
                                                                </label>
                                                            </div>
                                                        </td>
                                                        <td className="pe-16">
                                                            <span className="text-primary-light fw-semibold">
                                                                {state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL} ` : `${state?.SYMBOL} `} {invoiceData?.subtotal}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="pe-64">
                                                            <div className="d-flex align-items-center">
                                                                <label className="me-2 fw-600">
                                                                    {Languages[state?.LANGUAGE]?.discount}{" "}
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control-plaintext ms-1 form-text-color"
                                                                    value={invoiceData?.discountPercetage}
                                                                    readOnly
                                                                    onChange={(e) =>
                                                                        handleChange(
                                                                            "discountPercetage",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder="0"
                                                                    style={{
                                                                        fontSize: 14,
                                                                        width: 20,
                                                                        textAlign: "center",
                                                                    }}
                                                                />
                                                                {"(%)"}
                                                            </div>
                                                        </td>
                                                        <td className="pe-16">
                                                            <span className="text-primary-light fw-semibold">
                                                                {state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL} ` : `${state?.SYMBOL} `} {invoiceData?.discountAmount || "0.00"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="pe-64 border-bottom pb-4">
                                                            <div className="d-flex align-items-center">
                                                                <label className="me-2 fw-600">{state?.TAXTYPE}:  </label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control-plaintext ms-1 form-text-color"
                                                                    value={invoiceData?.gstPercentage}
                                                                    readOnly
                                                                    onChange={(e) => {
                                                                        handleChange(
                                                                            "gstPercentage",
                                                                            e.target.value
                                                                        );
                                                                    }}
                                                                    placeholder="0"
                                                                    style={{
                                                                        fontSize: 14,
                                                                        width: 20,
                                                                        textAlign: "center",
                                                                    }}
                                                                />
                                                                {"(%)"}
                                                            </div>
                                                        </td>
                                                        <td className="pe-16 border-bottom">
                                                            <span className="text-primary-light fw-semibold">
                                                                {state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL} ` : `${state?.SYMBOL} `} {invoiceData?.gstAmount || "0.00"}
                                                            </span>
                                                        </td>
                                                    </tr>

                                                    <tr>
                                                        <td className="pe-64 pt-4">
                                                            <span className="text-primary-light fw-semibold">
                                                                {Languages[state?.LANGUAGE]?.payable_amount}:

                                                            </span>
                                                        </td>
                                                        <td className="pe-16 pt-4">
                                                            <span className="text-primary-light fw-semibold">
                                                                {state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL} ` : `${state?.SYMBOL} `}{" "}
                                                                {invoiceData?.totalAmount || "0.00"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-4" style={{ marginTop: 10 }}>
                                                                {/* Checkbox */}
                                                                {state?.TAXTYPE == "GST" && isINR && (<div className="form-check checked-primary d-flex align-items-center gap-2">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id="igstCheckbox"
                                                                        checked={invoiceData.isigst}
                                                                        readOnly
                                                                    />
                                                                    <label
                                                                        className="form-check-label line-height-1 fw-medium text-secondary-light"
                                                                        htmlFor="igstCheckbox"
                                                                    >
                                                                        {Languages[state?.LANGUAGE].igst}
                                                                    </label>
                                                                </div>)}

                                                                {/* Switch */}
                                                                <div className="form-switch switch-success d-flex align-items-center gap-3">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        role="switch"
                                                                        id="horizontal3"
                                                                        checked={invoiceData.isPaid}
                                                                        readOnly
                                                                    />
                                                                    <label
                                                                        className="form-check-label line-height-1 fw-medium text-secondary-light"
                                                                        htmlFor="horizontal3"
                                                                        style={{ width: 80 }}
                                                                    >
                                                                        {invoiceData.isPaid ? Languages[state?.LANGUAGE].paid : Languages[state?.LANGUAGE].unpaid}
                                                                    </label>
                                                                </div>
                                                            </div>

                                                        </td>
                                                    </tr>
                                                </tbody >
                                            </table >
                                        </div >
                                    </div >
                                </div >
                                <div className='d-flex justify-content-between align-items-center'>

                                    {/* LEFT SIDE TEXT */}
                                    <div>
                                        {!invoiceData?.isPaid && (
                                            <p className="small text-muted mb-0">
                                                {Languages[state?.LANGUAGE]?.SaleReturn_Reason_note}
                                            </p>
                                        )}
                                    </div>

                                    {/* RIGHT SIDE BUTTONS */}
                                    <div className="d-flex gap-2">
                                        <button
                                            type="reset"
                                            onClick={() => navigate(-1)}
                                            className="border btn-sm border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-6 radius-8"
                                        >
                                            {Languages[state?.LANGUAGE]?.cancel}
                                        </button>

                                        <button
                                            type="button"
                                            className={`btn btn-sm ${isLimitReached ? "btn-secondary" : "btn-primary-600"} radius-8 d-inline-flex align-items-center px-40 py-6 gap-2`}
                                            onClick={!isLimitReached ? OnPressSave : undefined}
                                            disabled={isLimitReached}
                                            style={{ cursor: isLimitReached ? "not-allowed" : "pointer" }}
                                        >
                                            <Icon icon="simple-line-icons:check" className="text-xl" />
                                            {isLimitReached ? "Limit Reached" : Languages[state?.LANGUAGE]?.save}
                                        </button>
                                    </div>

                                </div>

                            </div >
                        </div >
                    </div >
                </div >
            </div >
            {isLoading && <LoaderView />}
        </div >

    );
};

export default SaleReturnAdd;